import { useMutation, useQueryClient } from '@tanstack/react-query'

import { pipelineApi } from '@/api/pipelineApi'
import { usePipelineSummary } from '@/features/actions/hooks/usePipelineSummary'
import { useToast, type ToastOptions } from '@/components/ui/Toast'
import type { LeadIntakeInput } from '@/features/pipeline/schemas/leadIntakeSchema'
import type { LeadIntakeResult } from '@/features/pipeline/types'
import { queryKeys } from '@/lib/query/queryKeys'

const FIELD_LABELS: Record<string, string> = {
  firstName: 'Vorname',
  lastName: 'Nachname',
  phone: 'Telefon',
}

function toast(result: LeadIntakeResult, summary: string | null): ToastOptions {
  const who = result.name ?? result.email
  // Whether the lead is due today, and when it is due next, is the part of the
  // response that tells the studio what to do with it.
  const next = summary ? ` · ${summary}` : ''

  if (result.created) {
    return {
      title: 'Lead angelegt',
      description: `${who} · ${result.pipeline.stageLabel}${next}`,
    }
  }

  const enriched = result.enrichedFields.map((field) => FIELD_LABELS[field] ?? field)
  return {
    title: 'Lead angereichert',
    description:
      enriched.length > 0
        ? `${who} war bereits erfasst · ergänzt: ${enriched.join(', ')}${next}`
        : `${who} war bereits erfasst · keine neuen Angaben${next}`,
  }
}

export function useSubmitLeadIntake() {
  const queryClient = useQueryClient()
  const summarise = usePipelineSummary()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: (input: LeadIntakeInput) => pipelineApi.submitLeadIntake(input),
    onSuccess: (result) => {
      showToast(toast(result, summarise(result.pipeline)))
      void queryClient.invalidateQueries({ queryKey: queryKeys.pipeline.all })
      // A new lead is due on the call list, and an enriched one changes its card.
      void queryClient.invalidateQueries({ queryKey: queryKeys.calls.all })
      // A lead is a customer: it joins the Kunden list (every page and search,
      // not just its own card) and the booking form's picker.
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduler.contacts() })
    },
  })
}
